import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export const Navigation = ({ onContactClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const solidNav = isScrolled || !isHomePage || isMobileMenuOpen;
  const useWhiteLogo = isHomePage && !isScrolled && !isMobileMenuOpen;
  const useWhiteText = isHomePage && !isScrolled && !isMobileMenuOpen;

  const navLinks = [
    { hash: '#why-bess', label: 'Warum BESS' },
    { hash: '#functions', label: 'Funktionen' },
    { hash: '#economics', label: 'Wirtschaftlichkeit' },
    { hash: '#case-studies', label: 'Fallstudien' },
  ];

  const handleNavClick = (e, hash) => {
    e.preventDefault();
    if (isHomePage) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/' + hash);
    }
  };

  const logoWhite = "https://customer-assets.emergentagent.com/job_462b1c9b-939c-4705-8ff2-b6f811719f70/artifacts/5fg8uv2b_battery-grid.io-3.svg";
  const logoColor = "https://customer-assets.emergentagent.com/job_462b1c9b-939c-4705-8ff2-b6f811719f70/artifacts/lkfe8exz_battery-grid.io.svg";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solidNav
          ? 'bg-background/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src={useWhiteLogo ? logoWhite : logoColor}
              alt="battery-grid.io"
              className="h-8 sm:h-14 transition-opacity duration-300"
              data-testid="nav-logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.hash}
                href={`/${link.hash}`}
                onClick={(e) => handleNavClick(e, link.hash)}
                className={`text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                  useWhiteText
                    ? 'text-white/80 hover:text-white'
                    : 'text-foreground/80 hover:text-primary'
                }`}
              >
                {link.label}
              </a>
            ))}
            <Button size="sm" className="ml-4" onClick={onContactClick}>
              Kontakt
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 ${useWhiteText ? 'text-white' : 'text-foreground'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.hash}
                  href={`/${link.hash}`}
                  className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-3 px-2 rounded-md hover:bg-muted"
                  onClick={(e) => { handleNavClick(e, link.hash); setIsMobileMenuOpen(false); }}
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-2">
                <Button size="sm" className="w-full" onClick={() => { onContactClick?.(); setIsMobileMenuOpen(false); }}>
                  Kontakt
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};